<div class="px-6 pt-6">
  <h2 class="text-2xl font-semibold text-gray-800 mb-3">
    {{ "sale_report.title" | translate }}
  </h2>
  <div class="flex">
    @for (tab of reportTabs; track tab.value; let i = $index) {
      <button
        type="button"
        [class]="
          'px-4 py-2 text-sm font-medium border-b-2 ' +
          (activeReportType() === tab.value
            ? 'border-blue-500 text-blue-600'
            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300')
        "
        (click)="onTabChange({ index: i })"
      >
        {{ tab.label | translate }}
      </button>
    }
  </div>
</div>

<div class="sale-report-container">
  <!-- Search Form Section -->
  <div class="search-section">
    <app-search-form
      [searchFormat]="getSearchConfig()"
      (formSearch)="onSearch($event)"
      (formReset)="onReset()"
    ></app-search-form>
  </div>

  <!-- Statistics Overview Section (only for statistics report) -->
  @if (activeReportType() === "statistics" && statisticsOverview()) {
    <div class="statistics-overview-section">
      <h2>{{ "sale_report.charts.kpis_overview" | translate }}</h2>

      <!-- KPI Panels - Horizontal Row Layout -->
      <div class="kpi-panels-row">
        @for (panel of kpiPanels; track panel.type) {
          <div class="kpi-panel-item" (click)="onPanelClick(panel.type)">
            <div class="kpi-icon" [class]="panel.iconClass">
              <i [class]="panel.icon" class="card-panel-icon"></i>
            </div>
            <div class="kpi-content">
              <div class="kpi-value">{{ panel.value | number }}</div>
              <div class="kpi-label">{{ panel.title | translate }}</div>
            </div>
          </div>
        }
      </div>
    </div>
  }

  <!-- Charts Section - Separate from table -->
  <div class="charts-section">
    <!-- Sales Statistics Charts -->
    @if (activeReportType() === "statistics") {
      <div class="statistics-charts">
        <div class="chart-row">
          <div class="chart-container">
            <h3>
              {{ "sale_report.charts.sales_trend_over_time" | translate }}
            </h3>
            <app-line-chart
              [chartData]="salesTrendChartData"
              [height]="'300px'"
            >
            </app-line-chart>
          </div>
          <div class="chart-container">
            <h3>
              {{ "sale_report.charts.sales_by_payment_method" | translate }}
            </h3>
            <app-pie-chart
              [chartData]="salesByPaymentMethodData"
              [height]="'300px'"
            >
            </app-pie-chart>
          </div>
        </div>
      </div>
    }

    <!-- Sales Analysis Charts -->
    @if (activeReportType() === "analysis") {
      <div class="analysis-charts">
        <div class="chart-row">
          <div class="chart-container">
            <h3>{{ "sale_report.charts.sales_by_region" | translate }}</h3>
            <app-bar-chart [chartData]="salesByRegionData" [height]="'300px'">
            </app-bar-chart>
          </div>
          <div class="chart-container">
            <h3>
              {{ "sale_report.charts.order_conversion_funnel" | translate }}
            </h3>
            <app-pie-chart [chartData]="orderConversionData" [height]="'300px'">
            </app-pie-chart>
          </div>
        </div>
      </div>
    }

    <!-- Product Sales Charts -->
    @if (activeReportType() === "products") {
      <div class="product-charts">
        <div class="chart-row">
          <div class="chart-container">
            <h3>
              {{
                "sale_report.charts.top_10_best_selling_products" | translate
              }}
            </h3>
            <app-bar-chart [chartData]="topProductsData" [height]="'300px'">
            </app-bar-chart>
          </div>
          <div class="chart-container">
            <h3>
              {{
                "sale_report.charts.bottom_10_slow_moving_products" | translate
              }}
            </h3>
            <app-bar-chart [chartData]="bottomProductsData" [height]="'300px'">
            </app-bar-chart>
          </div>
        </div>
      </div>
    }

    <!-- Shop Sales Charts -->
    @if (activeReportType() === "shops") {
      <div class="shop-charts">
        <div class="chart-row">
          <div class="chart-container">
            <h3>{{ "sale_report.charts.sales_by_shop" | translate }}</h3>
            <app-bar-chart [chartData]="topProductsData" [height]="'300px'">
            </app-bar-chart>
          </div>
          <div class="chart-container">
            <h3>
              {{ "sale_report.charts.shop_performance_radar" | translate }}
            </h3>
            <app-radar-chart
              [chartData]="shopPerformanceData"
              [height]="'300px'"
            >
            </app-radar-chart>
          </div>
        </div>
      </div>
    }
  </div>

  <!-- Data Table Section -->
  <div class="table-section">
    <!-- Sales Statistics Table -->
    @if (activeReportType() === "statistics") {
      <div class="table-header">
        <h2 class="table-title">{{ getTableTitle() }}</h2>
        <div class="table-actions">
          <p-button
            [label]="'sale_report.actions.export_excel' | translate"
            icon="pi pi-download"
            severity="info"
            size="small"
            (onClick)="onExport()"
          ></p-button>
        </div>
      </div>

      <app-data-table
        [dataSource]="statisticsData()"
        [columns]="getTableColumns()"
        [loading]="loading()"
        [totalRecords]="totalRecords()"
        [first]="first()"
        [rows]="rows()"
        (pageChange)="onPageChange($event)"
      >
        <!-- Custom template for formatting -->
        <ng-template #customTemplate let-value let-column="column">
          @if (column.prop === "orderDate") {
            {{ formatDate(value) }}
          } @else if (column.prop === "orderStatus") {
            <span [class]="'status-badge status-' + value.toLowerCase()">
              {{ value }}
            </span>
          } @else if (
            [
              "grossSales",
              "netSales",
              "totalCollected",
              "averageOrderValue",
            ].includes(column.prop)
          ) {
            {{ formatCurrency(value) }}
          } @else if (
            ["discount", "refund", "tax", "shippingFee"].includes(column.prop)
          ) {
            {{ formatCurrency(value) }}
          } @else if (["numberOfOrders"].includes(column.prop)) {
            {{ value | number }}
          } @else {
            {{ value }}
          }
        </ng-template>
      </app-data-table>
    }

    <!-- Sales Analysis Table -->
    @if (activeReportType() === "analysis") {
      <div class="table-header">
        <h2 class="table-title">{{ getTableTitle() }}</h2>
        <div class="table-actions">
          <p-button
            [label]="'sale_report.actions.export_excel' | translate"
            icon="pi pi-download"
            severity="info"
            size="small"
            (onClick)="onExport()"
          ></p-button>
        </div>
      </div>

      <app-data-table
        [dataSource]="analysisData()"
        [columns]="getTableColumns()"
        [loading]="loading()"
        [totalRecords]="totalRecords()"
        [first]="first()"
        [rows]="rows()"
        (pageChange)="onPageChange($event)"
      >
        <!-- Custom template for formatting -->
        <ng-template #customTemplate let-value let-column="column">
          @if (
            ["grossSales", "netSales", "averageOrderValue"].includes(
              column.prop
            )
          ) {
            {{ formatCurrency(value) }}
          } @else if (["totalOrders"].includes(column.prop)) {
            {{ value | number }}
          } @else if (
            ["conversionRate", "salesGrowthRate"].includes(column.prop)
          ) {
            {{ formatPercentage(value) }}
          } @else if (["refund"].includes(column.prop)) {
            {{ formatCurrency(value) }}
          } @else {
            {{ value }}
          }
        </ng-template>
      </app-data-table>
    }

    <!-- Product Sales Table -->
    @if (activeReportType() === "products") {
      <div class="table-header">
        <h2 class="table-title">{{ getTableTitle() }}</h2>
        <div class="table-actions">
          <p-button
            [label]="'sale_report.actions.export_excel' | translate"
            icon="pi pi-download"
            severity="info"
            size="small"
            (onClick)="onExport()"
          ></p-button>
        </div>
      </div>

      <app-data-table
        [dataSource]="productData()"
        [columns]="getTableColumns()"
        [loading]="loading()"
        [totalRecords]="totalRecords()"
        [first]="first()"
        [rows]="rows()"
        (pageChange)="onPageChange($event)"
      >
        <!-- Custom template for formatting -->
        <ng-template #customTemplate let-value let-column="column">
          @if (
            ["grossSales", "netSales", "unitPrice", "discountApplied"].includes(
              column.prop
            )
          ) {
            {{ formatCurrency(value) }}
          } @else if (
            ["quantitySold", "returnedQuantity"].includes(column.prop)
          ) {
            {{ value | number }}
          } @else if (column.prop === "topSellingFlag") {
            <span [class]="value ? 'top-selling-yes' : 'top-selling-no'">
              {{
                value
                  ? ("sale_report.values.yes" | translate)
                  : ("sale_report.values.no" | translate)
              }}
            </span>
          } @else {
            {{ value }}
          }
        </ng-template>
      </app-data-table>
    }

    <!-- Shop Sales Table -->
    @if (activeReportType() === "shops") {
      <div class="table-header">
        <h2 class="table-title">{{ getTableTitle() }}</h2>
        <div class="table-actions">
          <p-button
            [label]="'sale_report.actions.export_excel' | translate"
            icon="pi pi-download"
            severity="info"
            size="small"
            (onClick)="onExport()"
          ></p-button>
        </div>
      </div>

      <app-data-table
        [dataSource]="shopData()"
        [columns]="getTableColumns()"
        [loading]="loading()"
        [totalRecords]="totalRecords()"
        [first]="first()"
        [rows]="rows()"
        (pageChange)="onPageChange($event)"
      >
        <!-- Custom template for formatting -->
        <ng-template #customTemplate let-value let-column="column">
          @if (
            ["grossSales", "netSales", "shippingFee"].includes(column.prop)
          ) {
            {{ formatCurrency(value) }}
          } @else if (["numberOfOrders"].includes(column.prop)) {
            {{ value | number }}
          } @else if (["salesGrowthRate"].includes(column.prop)) {
            {{ formatPercentage(value) }}
          } @else if (["refund"].includes(column.prop)) {
            {{ formatCurrency(value) }}
          } @else {
            {{ value }}
          }
        </ng-template>
      </app-data-table>
    }
  </div>

  <!-- Toast Messages -->
  <p-toast></p-toast>

  <!-- Shared Confirmation Dialog -->
  <app-confirmation-dialog
    [(visible)]="showConfirmationDialog"
    [title]="dialogTitle"
    [message]="dialogMessage"
    [buttons]="dialogButtons"
    (emitOnClick)="onConfirmatiButtonClick($event)"
  ></app-confirmation-dialog>
</div>
